import { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { LogIn, User, AlertCircle } from 'lucide-react';
import { Button } from '@core/components/ui/shared/buttons/button';
import Label from '@core/components/ui/shared/labels/label';
import { FloatingInput } from '@core/components/ui/shared/inputs/floating-input';
import { PasswordInput } from '@/components/password-input';
import { Checkbox } from '@core/components/ui/shared/checkboxes/checkbox';
import { loginSchema, type LoginFormData } from '@/validations/schemas';
import useBackendLogin from '@/hooks/use-backend-login';
import useExternalProviders from '@/hooks/use-external-providers';
import { containerVariants, itemVariants } from '@/lib/animations';

interface Props {
  onForgotPassword: () => void;
}

const LoginForm = ({ onForgotPassword }: Props) => {
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const returnUrl = params.get('returnUrl') ?? '/';
  const { login, isLoading } = useBackendLogin(returnUrl);
  const { providers } = useExternalProviders();

  const externalError = params.get('externalError');
  const externalErrorMessage = useMemo(() => {
    if (!externalError) return null;
    const errorMessages: Record<string, string> = {
      user_not_found: t('errors.userNotFound'),
      external_auth_failed: t('errors.externalAuthFailed'),
      account_locked: t('errors.accountLocked'),
    };
    return errorMessages[externalError] ?? null;
  }, [externalError, t]);

  const handleExternalLogin = (scheme: string) => {
    window.location.href = `/signIn/external?provider=${scheme}&returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      login: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (data: LoginFormData): Promise<void> => {
    await login({
      username: data.login,
      password: data.password,
      rememberMe: data.rememberMe,
    });
  }

  return (
    <motion.div
      className="space-y-5 sm:space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <AnimatePresence>
        {externalErrorMessage && (
          <motion.div
            variants={itemVariants}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{externalErrorMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div variants={itemVariants} className="text-center space-y-1.5">
        <motion.div
          className="flex justify-center mb-3"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          <div className="p-3 rounded-full bg-primary/10">
            <User className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
          </div>
        </motion.div>
        <h1 className="text-xl font-bold sm:text-2xl">{t('signIn')}</h1>
        <p className="text-xs text-muted-foreground sm:text-sm">
          {t('signInSubtitle')}
        </p>
      </motion.div>

      {providers.length > 0 && (
        <>
          <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-3">
            {providers.map((provider) => (
              <motion.button
                key={provider.scheme}
                type="button"
                className="inline-flex items-center justify-center rounded-full border border-border bg-background p-1.5 shadow-sm transition-colors hover:bg-accent hover:border-accent-foreground/20"
                onClick={() => handleExternalLogin(provider.scheme)}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                title={provider.displayName ?? provider.scheme}
              >
                <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden flex items-center justify-center">
                  <img
                    src={`https://cdn.loyaltydrive.pl/lde/${provider.displayName}.png`}
                    alt={provider.displayName ?? provider.scheme}
                    className="w-full h-full object-contain"
                  />
                </span>
              </motion.button>
            ))}
          </motion.div>

          <motion.div variants={itemVariants} className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-3 text-muted-foreground tracking-wider">
                {t('orSignInWith')}
              </span>
            </div>
          </motion.div>
        </>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 sm:space-y-4">
        <motion.div variants={itemVariants}>
          <Controller
            name="login"
            control={form.control}
            render={({ field }) => (
              <FloatingInput
                id="login"
                label={t('login')}
                className="h-11 sm:h-12 text-sm sm:text-base"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                aria-invalid={!!form.formState.errors.login}
                readOnly={isLoading}
              />
            )}
          />
          {form.formState.errors.login && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1.5 text-xs text-destructive"
            >
              {t(form.formState.errors.login.message!)}
            </motion.p>
          )}
        </motion.div>

        <motion.div variants={itemVariants}>
          <Controller
            name="password"
            control={form.control}
            render={({ field }) => (
              <PasswordInput
                id="password"
                label={t('password')}
                className="h-11 sm:h-12 text-sm sm:text-base"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                aria-invalid={!!form.formState.errors.password}
                readOnly={isLoading}
              />
            )}
          />
          {form.formState.errors.password && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1.5 text-xs text-destructive"
            >
              {t(form.formState.errors.password.message!)}
            </motion.p>
          )}
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="flex items-center justify-between pt-0.5"
        >
          <Controller
            name="rememberMe"
            control={form.control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="rememberMe"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="size-4"
                />
                <Label
                  htmlFor="rememberMe"
                  className="text-xs sm:text-sm text-muted-foreground cursor-pointer select-none"
                >
                  {t('rememberMe')}
                </Label>
              </div>
            )}
          />

          <motion.button
            type="button"
            onClick={onForgotPassword}
            className="text-xs sm:text-sm text-primary hover:text-primary/80 font-medium transition-colors"
            whileHover={{ x: 3 }}
            whileTap={{ scale: 0.98 }}
          >
            {t('forgotPassword')}
          </motion.button>
        </motion.div>

        <motion.div variants={itemVariants} className="pt-1 sm:pt-2">
          <Button
            type="submit"
            size="lg"
            className="w-full h-11 sm:h-12 rounded-full text-sm sm:text-base font-semibold group"
            disabled={isLoading}
          >
            {isLoading ? (
              <motion.div
                className="h-5 w-5 border-2 border-current border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
            ) : (
              <>
                <LogIn className="mr-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                {t('signIn')}
              </>
            )}
          </Button>
        </motion.div>
      </form>
    </motion.div>
  );
}

export default LoginForm;
